class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

 onSubmitHandler(event) {
  const form = event.target.closest('form');
  const sortSelect = this.querySelector('select[name="sort_by"]');
if (sortSelect) {
  sortSelect.addEventListener('change', () => {
    sortSelect.dataset.userChanged = 'true';
  });
}
  form.submit();
}

 onActiveFilterClick(event) {
  window.location.href = event.currentTarget.href;
}
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.minFloor = 1000;
    this.stepSize = 500;
    this.onInputBound = this.onInput.bind(this);
  }

  connectedCallback() {
    this.minInput = this.querySelector('.range-min');
    this.maxInput = this.querySelector('.range-max');
    if (!this.minInput || !this.maxInput) return;

    this.fill = this.querySelector('.track-fill');

    // Moving bubbles
    this.bubbleMin = this.querySelector('.bubble-min');
    this.bubbleMax = this.querySelector('.bubble-max');

    // Fixed labels
    this.fixedMin = this.querySelector('.fixed-min');
    this.fixedMax = this.querySelector('.fixed-max');

    this.normalize(this.minInput);
    this.normalize(this.maxInput);

    this.minInput.removeEventListener('input', this.onInputBound);
    this.maxInput.removeEventListener('input', this.onInputBound);

    this.minInput.addEventListener('input', this.onInputBound);
    this.maxInput.addEventListener('input', this.onInputBound);

    this.adjustValues(this.minInput);
    this.updateUI();
  }

  normalize(inp) {
    if (!inp.min || isNaN(Number(inp.min))) inp.min = String(this.minFloor);
    if (!inp.step || inp.step === 'any' || Number(inp.step) <= 0) inp.step = String(this.stepSize);
    if (!Number.isFinite(Number(inp.value))) inp.value = inp.min;
  }

  snap(val, min, max) {
    val = Math.max(min, Math.min(max, val));
    return Math.round(val / this.stepSize) * this.stepSize;
  }

  onInput(e) {
    this.adjustValues(e.currentTarget);
    this.updateUI();
  }

  adjustValues(changed) {
    const min = Number(this.minInput.min) || this.minFloor;
    const max = Number(this.maxInput.max);
    if (!Number.isFinite(max) || max <= min) return;

    let a = this.snap(Number(this.minInput.value), min, max);
    let b = this.snap(Number(this.maxInput.value), min, max);

    if (a > b) {
      if (changed === this.minInput) a = b;
      else b = a;
    }

    this.minInput.value = String(a);
    this.maxInput.value = String(b);
  }

  updateUI() {
    this.updateFill();
    this.updateBubbles();     // moving bubbles
    this.updateFixedLabels(); // fixed left/right prices
  }

  updateFill() {
    if (!this.fill) return;

    const min = Number(this.minInput.min) || this.minFloor;
    const max = Number(this.maxInput.max);
    const a = Number(this.minInput.value);
    const b = Number(this.maxInput.value);

    const left = ((a - min) / (max - min)) * 100;
    const right = ((b - min) / (max - min)) * 100;

    this.fill.style.left = `${left}%`;
    this.fill.style.width = `${right - left}%`;
  }

  updateFixedLabels() {
    if (!this.fixedMin || !this.fixedMax) return;
    this.fixedMin.textContent = `₹${Number(this.minInput.value)}`;
    this.fixedMax.textContent = `₹${Number(this.maxInput.value)}`;
  }

  updateBubbles() {
    if (!this.bubbleMin || !this.bubbleMax) return;

    const min = Number(this.minInput.min) || this.minFloor;
    const max = Number(this.maxInput.max);
    if (!Number.isFinite(max) || max <= min) return;

    const slider = this.minInput;
    const sliderRect = slider.getBoundingClientRect();
    const containerRect = this.querySelector('.price-slider').getBoundingClientRect();

    const width = sliderRect.width;
    const offsetLeft = sliderRect.left - containerRect.left;

    const aVal = Number(this.minInput.value);
    const bVal = Number(this.maxInput.value);

    // set text before measuring
    this.bubbleMin.textContent = `₹${aVal}`;
    this.bubbleMax.textContent = `₹${bVal}`;

    this.bubbleMin.classList.remove('is-stacked');
    this.bubbleMax.classList.remove('is-stacked');

    const posFor = (val, bubble) => {
      const percent = (val - min) / (max - min);
      const x = percent * width;

      const bw = bubble.offsetWidth;
      let leftPx = x - bw / 2;

      leftPx = Math.max(0, Math.min(width - bw, leftPx));
      return leftPx;
    };

    let minLeft = posFor(aVal, this.bubbleMin);
    let maxLeft = posFor(bVal, this.bubbleMax);

    const minW = this.bubbleMin.offsetWidth;
    const maxW = this.bubbleMax.offsetWidth;
    const gap = 8;

    const overlaps = !(minLeft + minW + gap <= maxLeft || maxLeft + maxW + gap <= minLeft);

    if (overlaps) {
      const centerMin = minLeft + minW / 2;
      const centerMax = maxLeft + maxW / 2;

      const required = (minW / 2) + (maxW / 2) + gap;
      let mid = (centerMin + centerMax) / 2;

      let newMinCenter = mid - required / 2;
      let newMaxCenter = mid + required / 2;

      newMinCenter = Math.max(minW / 2, Math.min(width - minW / 2, newMinCenter));
      newMaxCenter = Math.max(maxW / 2, Math.min(width - maxW / 2, newMaxCenter));

      minLeft = newMinCenter - minW / 2;
      maxLeft = newMaxCenter - maxW / 2;

      const stillOverlaps = !(minLeft + minW + gap <= maxLeft || maxLeft + maxW + gap <= minLeft);
      if (stillOverlaps) {
        this.bubbleMax.classList.add('is-stacked');
        minLeft = posFor(aVal, this.bubbleMin);
        maxLeft = posFor(bVal, this.bubbleMax);
      }
    }

    this.bubbleMin.style.left = `${offsetLeft + minLeft}px`;
    this.bubbleMax.style.left = `${offsetLeft + maxLeft}px`;
  }
}

if (!customElements.get('price-range')) {
  customElements.define('price-range', PriceRange);
}





class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
